import React, { useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import SolarLease from 'src/models/contract_types/SolarLease';
import IndexReferenceType from 'src/models/IndexReferenceType';
import MathUtil from 'src/util/MathUtil';

interface ISolarLeaseGenerationProps extends Partial<Pick<UseFormMethods, 'getValues' | 'setValue' | 'register' | 'errors'>> {
    solarLeaseData: SolarLease;
    measurementTypes: Array<IndexReferenceType>;
    isInHanded: boolean;
    totalGross?: number;
}

const SolarLeaseGeneration = (props: ISolarLeaseGenerationProps) => {
    const [isAddConsideration, setIsAddConsideration] = useState<boolean>(false);
    // const [isAddOptionToExtend, setIsAddOptionToExtend] = useState<boolean>(false);

    useEffect(() => {
        if (!!props.solarLeaseData) {
            onUpdateFields(true);
        }
    }, [props]);
    
    const onUpdateFields = (initialize: boolean) => {
        if (initialize === true) {
            setIsAddConsideration(props.solarLeaseData.isAddConsideration === true);
            // setIsAddOptionToExtend(props.solarLeaseData.isAddOptionToExtend === true);
        } else {
            let ref = props.getValues();
            if (!!ref.solarLeaseHolds?.[0]) {
                setIsAddConsideration(ref.solarLeaseHolds[0].isAddConsideration === true);
                // setIsAddOptionToExtend(ref.batteryStorageHolds[0].isAddOptionToExtend === true);
            }
        }
    };
    
    const updateTotalConsideration = () => {
        let ref = props.getValues();
        let totalBonus: number = ref?.totalBonus ? parseFloat(ref?.totalBonus + '') : 0;

        let considerationAmount: number = parseFloat(ref?.addConsiderationAmount + '');

        if (!isNaN(considerationAmount) && totalBonus === 0) {
            props.setValue('totalConsideration', considerationAmount);
        }

        if (!isNaN(considerationAmount) && totalBonus !== 0) {
            props.setValue('totalConsideration', totalBonus + considerationAmount);
        } else {
            props.setValue('totalConsideration', '0');
        }
    };

    const calculateTotal = (
        measurementTypeId: number,
        paymentAmount: number,
        grossAcres: number,
        measurementTypesList: Array<IndexReferenceType>
    ) => {
        let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

        return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    };

    const handleTotals = () => {
        let rep = props.getValues();
        let totalBonus = 0;
        let grossAcres = props.totalGross;

        let bonusTypeId = parseInt(rep?.bonusTypeId, 10);
        let bonusAmount = parseFloat(rep?.bonusAmount);
        if (!isNaN(bonusTypeId) && !isNaN(bonusAmount)) {
            let initialBonus = calculateTotal(bonusTypeId, bonusAmount, grossAcres, props.measurementTypes);
            props.setValue('initialBonus', initialBonus);
            totalBonus += initialBonus;
        }

        let rentalTypeId = parseInt(rep?.rentalTypeId, 10);
        let rentAmount = parseFloat(rep?.rentalAmount);
        if (!isNaN(rentalTypeId) && !isNaN(rentAmount)) {
            let totalRent = calculateTotal(rentalTypeId, rentAmount, grossAcres, props.measurementTypes);
            props.setValue('rentTotal', totalRent);
            totalBonus += totalRent;
        }

        let flatBonus = !isNaN(parseFloat(rep?.flatBonus)) ? parseFloat(rep?.flatBonus) : 0;
        let additionalBonus = !isNaN(parseFloat(rep?.addlBonusAmount)) ? parseFloat(rep?.addlBonusAmount) : 0;

        totalBonus += flatBonus + additionalBonus;
        props.setValue('totalBonus', totalBonus);

        let dueDiligenceType = parseInt(rep?.currentHold?.dueDiligenceFeeTypeId, 10);
        let dueDiligence = parseFloat(rep?.currentHold?.dueDiligenceFee);
        if (!isNaN(dueDiligence) && !isNaN(dueDiligenceType)) {
            props.setValue('currentHold.dueDiligenceFeeTotal', calculateTotal(dueDiligenceType, dueDiligence, grossAcres, props.measurementTypes));
        }

        let dueDiligenceExtensionFeeType = parseInt(rep?.currentHold?.dueDiligenceExtensionFeeTypeId, 10);
        let dueDiligenceExtensionFee = parseFloat(rep?.currentHold?.dueDiligenceExtensionFee);
        if (!isNaN(dueDiligenceExtensionFee) && !isNaN(dueDiligenceExtensionFeeType)) {
            props.setValue(
                'currentHold.dueDiligenceExtensionFeeTotal',
                calculateTotal(dueDiligenceExtensionFeeType, dueDiligenceExtensionFee, grossAcres, props.measurementTypes)
            );
        }

        let constructionFeeType = parseInt(rep?.currentHold?.constructionFeeTypeId, 10);
        let constructionFee = parseFloat(rep?.currentHold?.constructionFee);
        if (!isNaN(constructionFee) && !isNaN(constructionFeeType)) {
            props.setValue('currentHold.constructionFeeTotal', calculateTotal(constructionFeeType, constructionFee, grossAcres, props.measurementTypes));
        }

        let operatingIPFFee = parseInt(rep?.currentHold?.operatingIPFFee, 10);
        let operatingIPFFeeType = parseFloat(rep?.currentHold?.operatingIPFFeeTypeId);
        if (!isNaN(operatingIPFFee) && !isNaN(operatingIPFFeeType)) {
            props.setValue('currentHold.operatingIPFFeeTotal', calculateTotal(operatingIPFFeeType, operatingIPFFee, grossAcres, props.measurementTypes));
        }

        let operatingOPFFee = parseInt(rep?.currentHold?.operatingOPFFee, 10);
        let operatingOPFFeeType = parseFloat(rep?.currentHold?.operatingOPFFeeTypeId);
        if (!isNaN(operatingOPFFee) && !isNaN(operatingOPFFeeType)) {
            props.setValue('currentHold.operatingOPFFeeTotal', calculateTotal(operatingOPFFeeType, operatingOPFFee, grossAcres, props.measurementTypes));
        }

        if (!!rep?.rentalAmount && !!rep?.rentalPaymentUnit) {
            props.setValue('rentalTotalRent', parseFloat(rep?.rentalAmount + ''));
        }
    };

    const calcExpirationDate = (
        effectiveDateField: string,
        termField: string,
        termTypeField: string,
        measurementTypesList: Array<IndexReferenceType>
    ) => {
        const term = !isNaN(parseInt(termField, 10)) ? parseInt(termField, 10) : undefined;
        const termUnit = termTypeField ? termTypeField : undefined;
        const effectiveDate = effectiveDateField ? new Date(effectiveDateField) : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate) {
            console.log('Measurement Types during calc:', measurementTypesList);
            let termString = measurementTypesList.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
            switch (termString) {
                case 'Years':
                    return new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0];
                case 'Months':
                    return new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0];
                case 'Days':
                    return new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0];
                default:
                    return '';
            }
        } else {
            return '';
        }
    };

    const handleExpirationChange = (type: string) => {
        let rep = props.getValues();

        switch (type) {
            case 'primary':
                props.setValue('expirationDate', calcExpirationDate(rep.effectiveDate, rep.terms, rep.termsTypeId, props.measurementTypes));
                break;
            case 'construction':
                props.setValue(
                    'currentHold.constructionExpirationDate',
                    calcExpirationDate(
                        rep?.currentHold?.constructionEffectiveDate,
                        rep?.currentHold?.constructionTerm,
                        rep?.currentHold?.constructionTermTypeId,
                        props.measurementTypes
                    )
                );
                break;
            case 'effective':
                props.setValue(
                    'currentHold.dueDiligenceEffectiveExpirationDate',
                    calcExpirationDate(
                        rep?.currentHold?.dueDiligenceEffectiveDate,
                        rep?.currentHold?.dueDiligenceEffectiveTerm,
                        rep?.currentHold?.dueDiligenceEffectiveTermTypeId,
                        props.measurementTypes
                    )
                );
                break;
            case 'operation':
                props.setValue(
                    'currentHold.operationExpirationDate',
                    calcExpirationDate(
                        rep?.currentHold?.operationEffectiveDate,
                        rep?.currentHold?.operationTerm,
                        rep?.currentHold?.operationTermTypeId,
                        props.measurementTypes
                    )
                );
                break;
            case 'operationExtension':
                props.setValue(
                    'currentHold.operationExtensionExpirationDate',
                    calcExpirationDate(
                        rep?.currentHold?.operationExtensionEffectiveDate,
                        rep?.currentHold?.operationExtensionTerm,
                        rep?.currentHold?.operationExtensionTermTypeId,
                        props.measurementTypes
                    )
                );
                break;
            case 'option':
                props.setValue(
                    'currentHold.optionExpirationDate',
                    calcExpirationDate(
                        rep?.currentHold?.optionEffectiveDate,
                        rep?.currentHold?.optionTerm,
                        rep?.currentHold?.optionTermTypeId,
                        props.measurementTypes
                    )
                );
                break;
            default:
                break;
        }
    };

    const getOptions = (type: string, item: string = '') => {
        if (props.measurementTypes?.length > 0) {
            return props.measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    return (
        <div>
            <div style={{ margin: '1rem' }}>
                <div style={{ height: '100%', gap: '2rem' }}>
                    <div id="formContent">
                        <div>
                            <div>
                                <div style={{ marginBottom: '2rem' }}>
                                    <RHFLegend title="Contract Terms" />

                                    <div style={{ margin: 'auto 2rem' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Effective Date"
                                                name="effectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('effective')}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Due Diligence Date"
                                                name="currentHold.dueDiligenceEffectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('effective')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Due Diligence Term"
                                                name="currentHold.dueDiligenceEffectiveTerm"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('effective')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.dueDiligenceEffectiveTermTypeId"
                                                options={getOptions('Time')}
                                                onChange={() => handleExpirationChange('effective')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Exp. Date"
                                                name="currentHold.dueDiligenceEffectiveExpirationDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Construction Date"
                                                name="currentHold.constructionEffectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('construction')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Construction Term"
                                                name="currentHold.constructionTerm"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('construction')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.constructionTermTypeId"
                                                options={getOptions('Time')}
                                                onChange={() => handleExpirationChange('construction')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Construction Exp. Date"
                                                name="currentHold.constructionExpirationDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <div hidden={true}>
                                                <RHFInputBox
                                                    label=""
                                                    name="currentHold.id"
                                                    register={props.register}
                                                    type="number"
                                                    disabled={true}
                                                    errors={props.errors}
                                                />
                                                <RHFInputBox
                                                    label=""
                                                    name="currentHold.solarLeaseId"
                                                    register={props.register}
                                                    type="number"
                                                    disabled={true}
                                                    errors={props.errors}
                                                />
                                            </div>
                                            <RHFInputBox
                                                label="Operation Date"
                                                name="currentHold.operationEffectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('operation')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Operation Term"
                                                name="currentHold.operationTerm"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('operation')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.operationTermTypeId"
                                                options={getOptions('Time')}
                                                onChange={() => handleExpirationChange('operation')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Operation Exp. Date"
                                                name="currentHold.operationExpirationDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Operation Extension Date"
                                                name="currentHold.operationExtensionEffectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('operationExtension')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Operation Term"
                                                name="currentHold.operationExtensionTerm"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('operationExtension')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.operationExtensionTermTypeId"
                                                options={getOptions('Time')}
                                                onChange={() => handleExpirationChange('operationExtension')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Extension Exp. Date"
                                                name="currentHold.operationExtensionExpirationDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Option Date"
                                                name="currentHold.optionEffectiveDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('option')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Option Term"
                                                name="currentHold.optionTerm"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={() => handleExpirationChange('option')}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.optionTermTypeId"
                                                options={getOptions('Time')}
                                                onChange={() => handleExpirationChange('option')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Exp. Date"
                                                name="currentHold.optionExpirationDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Due Diligence Fee"
                                                name="currentHold.dueDiligenceFee"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.dueDiligenceFeeTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Total"
                                                name="currentHold.dueDiligenceFeeTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Construction Fee"
                                                name="currentHold.constructionFee"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.constructionFeeTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Total"
                                                name="currentHold.constructionFeeTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Operating IPF Fee"
                                                name="currentHold.operatingIPFFee"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.operatingIPFFeeTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Total"
                                                name="currentHold.operatingIPFFeeTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Due Diligence Ext. Fee"
                                                name="currentHold.dueDiligenceExtensionFee"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.dueDiligenceExtensionFeeTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Total"
                                                name="currentHold.dueDiligenceExtensionFeeTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Operating OPF Fee"
                                                name="currentHold.operatingOPFFee"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="currentHold.operatingOPFFeeTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Total"
                                                name="currentHold.operatingOPFFeeTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Bonus"
                                                name="bonusAmount"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="bonusTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Escalating Pct"
                                                name="currentHold.escalatingPct"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Rentals"
                                                name="rentalAmount"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                onChange={handleTotals}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="rentalTypeId"
                                                options={getOptions('Fee', 'acre')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label="Rental Frequency"
                                                name="rentalFrequencyId"
                                                options={getOptions('Frequency')}
                                                onChange={handleTotals}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Rental Terms"
                                                name="rentalTerms"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFSelect
                                                label=""
                                                name="rentalTermTypeId"
                                                options={getOptions('Time')}
                                                register={props.register}
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                            <RHFInputBox
                                                label="Rent Due Date"
                                                name="rentDueDate"
                                                register={props.register}
                                                type="date"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>

                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                justifyContent: 'space-between',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            <RHFInputBox
                                                label="Initial Bonus"
                                                name="initialBonus"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                            <RHFInputBox
                                                label="Rent Total"
                                                name="rentTotal"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                            />
                                            <RHFInputBox
                                                label="Total Bonus"
                                                name="totalBonus"
                                                register={props.register}
                                                type="number"
                                                errors={props.errors}
                                                bodyStyle={{ width: '150px' }}
                                                disabled={true}
                                                defaultVal="0"
                                            />
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            justifyContent: 'space-between',
                                            marginBottom: '2rem',
                                            height: '50px',
                                        }}
                                    >
                                        <RHFCheckbox
                                            name="isAddConsideration"
                                            label="Additional Consideration"
                                            register={props.register}
                                            errors={props.errors}
                                            bodyStyle={{ width: '200px', display: 'flex', alignItems: 'center', gap: '1rem' }}
                                            disabled={props.isInHanded}
                                            onClick={() => onUpdateFields(false)}
                                        />

                                        {isAddConsideration && (
                                            <>
                                                <RHFInputBox
                                                    name="addConsiderationExplanation"
                                                    label="Add Consideration Explanation"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '400px' }}
                                                    style={{ width: '400px' }}
                                                />
                                                <RHFInputBox
                                                    name="addConsiderationAmount"
                                                    label="Add Consideration Amount"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '180px' }}
                                                    style={{ width: 'auto' }}
                                                    defaultVal="0"
                                                    onChange={updateTotalConsideration}
                                                />
                                            </>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '2rem' }}>
                                        <RHFInputBox
                                            name="totalConsideration"
                                            label="Total Consideration"
                                            register={props.register}
                                            errors={props.errors}
                                            bodyStyle={{ width: '150px' }}
                                            defaultVal="0"
                                            disabled={props.isInHanded}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            justifyContent: 'space-between',
                                            rowGap: '2rem',
                                            margin: '1rem 1rem 0 1rem',
                                        }}
                                    >
                                        {/* Option to Extend req'mts not established yet. */}
                                        {/* <RHFCheckbox 
                                            name="isAddOptionToExtend" 
                                            label="Option to Extend" 
                                            register={props.register} 
                                            errors={props.errors} 
                                            onClick={() => onUpdateFields(false)}
                                        />
                                        {isAddOptionToExtend && (
                                            <>
                                                <RHFInputBox
                                                    name="addOptionBonusAmount"
                                                    label="Add Option Bonus Amount"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '150px' }}
                                                />
                                                <RHFInputBox
                                                    name="addOptionBonusUnit"
                                                    label="Add Option Bonus Unit"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '150px' }}
                                                />
                                                <RHFInputBox
                                                    name="addOptionTotalBonus"
                                                    label="Add Total Option Bonus"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '150px' }}
                                                />
                                                <RHFInputBox
                                                    name="addOptionTerm"
                                                    label="Add Option Term"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '150px' }}
                                                />
                                                <RHFInputBox
                                                    name="addOptionTermUnits"
                                                    label="Add Option Term Unit"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '150px' }}
                                                />
                                                <RHFCheckbox
                                                    name="prepaidOption"
                                                    label="Prepaid Option"
                                                    register={props.register}
                                                    errors={props.errors}
                                                    bodyStyle={{ width: '200px', display: 'flex', alignItems: 'center', gap: '1rem' }}
                                                    disabled={props.isInHanded}
                                                    onClick={() => onUpdateFields(false)}
                                                />
                                            </>
                                        )} */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SolarLeaseGeneration;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/SolarLeaseComponents/SolarLeaseGeneration.tsx